import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/store/cartStore";
import { useAuth } from "@/store/authStore";
import { toast } from "react-hot-toast";
import { ordersApi } from "@/services/api";
import { CreateOrderData } from "@/services/types/domain";

// Usa tus componentes de UI
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

const Divider: React.FC = () => (<hr className="my-5 border-gray-200" />);

type OrderResponse = {
    order: { id: number | string };
    payment?: { id: string; transaction_id: string };
};

// ===========================================================
// CheckoutPage adaptado a CreateOrderData + métodos Culqi/Yape
// ===========================================================
const CheckoutPage: React.FC = () => {
    const navigate = useNavigate();
    const { cart, isLoading: cartLoading, refreshCart } = useCart();
    const { user } = useAuth();

    // ===== Estado UI
    const [isProcessing, setIsProcessing] = useState(false);
    const [hydrating, setHydrating] = useState(true);

    // ===== Estado del formulario EXACTO a CreateOrderData
    const [form, setForm] = useState<CreateOrderData>({
        shipping_address: {
            recipient_name: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
            phone: "",
            country: "Perú",
            state: "",
            city: "",
            postal_code: "",
            address_line_1: "",
            address_line_2: "",
        },
        // Métodos soportados: credit_card, debit_card, paypal, cash, culqi, yape
        payment_method: "credit_card",
        notes: "",
    });

    // Datos locales de tarjeta / pasarela (NO van al payload de CreateOrderData)
    const [card, setCard] = useState({
        number: "",
        name: user ? `${user.first_name} ${user.last_name}`.trim() : "",
        expiry: "",
        cvv: "",
    });
    const [yapePhone, setYapePhone] = useState("");

    // Hidratar carrito al montar para evitar redirección prematura
    useEffect(() => {
        (async () => {
            try { await refreshCart(); } finally { setHydrating(false); }
        })();
    }, [refreshCart]);

    // Si tras hidratar no hay items, regresamos al carrito
    useEffect(() => {
        if (!hydrating && !cartLoading && (!cart || cart.items.length === 0)) {
            navigate("/cart", { replace: true });
        }
    }, [hydrating, cartLoading, cart, navigate]);

    const subtotal = useMemo(() => cart?.totals?.subtotal ?? 0, [cart]);
    const shipping = useMemo(() => cart?.totals?.shipping ?? 0, [cart]);
    const tax = useMemo(() => cart?.totals?.tax ?? 0, [cart]);
    const discount = useMemo(() => cart?.totals?.discount ?? 0, [cart]);
    const total = useMemo(() => cart?.totals?.total ?? 0, [cart]);

    const onChange = (
        path: keyof CreateOrderData["shipping_address"] | "payment_method" | "notes",
        value: string
    ) => {
        if (path === "payment_method" || path === "notes") {
            setForm((prev) => ({ ...prev, [path]: value }));
            return;
        }
        setForm((prev) => ({
            ...prev,
            shipping_address: { ...prev.shipping_address, [path]: value }
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!cart || cart.items.length === 0) return; // guard extra

        // Validaciones mínimas
        const { recipient_name, phone, country, state, city, postal_code, address_line_1 } = form.shipping_address;
        if (!recipient_name || !phone || !country || !state || !city || !postal_code || !address_line_1) {
            toast.error("Completa los campos obligatorios de envío");
            return;
        }

        // Validaciones según método de pago
        if (["credit_card", "debit_card", "culqi"].includes(form.payment_method)) {
            if (!card.number || !card.name || !card.expiry || !card.cvv) {
                toast.error("Completa los datos de la tarjeta");
                return;
            }
        }
        if (form.payment_method === "yape" && !yapePhone) {
            toast.error("Ingresa el teléfono asociado a Yape");
            return;
        }

        try {
            setIsProcessing(true);
            // Enviar EXACTAMENTE el shape que el backend espera
            const payload: CreateOrderData = {
                shipping_address: { ...form.shipping_address },
                payment_method: form.payment_method,
                notes: form.notes?.trim() ? form.notes : undefined,
            };

            // ordersApi.createOrder devuelve response.data.data -> { order, payment }
            const data = (await ordersApi.createOrder(payload)) as unknown as OrderResponse;

            if (data?.order?.id !== undefined && data?.order?.id !== null) {
                toast.success("¡Orden creada exitosamente!");
                navigate(`/order-confirmation/${String(data.order.id)}`);
            } else {
                throw new Error("Respuesta inesperada del servidor al crear orden");
            }
        } catch (err: any) {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message || "Error al procesar el pedido");
        } finally {
            setIsProcessing(false);
        }
    };

    if (hydrating || cartLoading) {
        return (
            <div className="min-h-screen grid place-items-center">
                <span className="text-gray-500">Cargando carrito…</span>
            </div>
        );
    }
    if (!cart || cart.items.length === 0) return null;

    //const paymentMethods: string[] = ["debit_card", "culqi", "yape", "cash", "cash_on_delivery"];
    //pamymentmethoinds in object
    const paymentMethodsObject: Record<string, string> = {
        //"credit_card": "Tarjeta de crédito",
        "debit_card": "Tarjeta de dédito",
        //"culqi": "Culqi",
        "yape": "Yape",
        "cash": "Efectivo",
        "cash_on_delivery": "Efectivo en entrega",
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="mb-6">
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Checkout</h1>
                    <p className="text-gray-600 mt-1">Completa tu información para finalizar la compra</p>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna izquierda: formulario */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                            <div className="px-5 pt-5 text-base font-semibold">Dirección de envío</div>
                            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <label htmlFor="recipient_name" className="block text-sm font-medium text-gray-700 mb-1">Nombre del destinatario *</label>
                                    <Input id="recipient_name" value={form.shipping_address.recipient_name} onChange={(e) => onChange("recipient_name", e.target.value)} placeholder="Ej. Ana Pérez" required />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                                    <Input id="phone" value={form.shipping_address.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="+51 999 999 999" required />
                                </div>
                                <div>
                                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">País *</label>
                                    <Input id="country" value={form.shipping_address.country} onChange={(e) => onChange("country", e.target.value)} placeholder="Perú" required />
                                </div>
                                <div>
                                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">Departamento/Estado *</label>
                                    <Input id="state" value={form.shipping_address.state} onChange={(e) => onChange("state", e.target.value)} placeholder="Lima" required />
                                </div>
                                <div>
                                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">Ciudad *</label>
                                    <Input id="city" value={form.shipping_address.city} onChange={(e) => onChange("city", e.target.value)} placeholder="Miraflores" required />
                                </div>
                                <div>
                                    <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">Código Postal *</label>
                                    <Input id="postal_code" value={form.shipping_address.postal_code} onChange={(e) => onChange("postal_code", e.target.value)} placeholder="15074" required />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="address_line_1" className="block text-sm font-medium text-gray-700 mb-1">Dirección línea 1 *</label>
                                    <Input id="address_line_1" value={form.shipping_address.address_line_1} onChange={(e) => onChange("address_line_1", e.target.value)} placeholder="Av. Ejemplo 123, Piso 4" required />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="address_line_2" className="block text-sm font-medium text-gray-700 mb-1">Dirección línea 2</label>
                                    <Input id="address_line_2" value={form.shipping_address.address_line_2} onChange={(e) => onChange("address_line_2", e.target.value)} placeholder="Referencia, departamento, etc." />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                            <div className="px-5 pt-5 text-base font-semibold">Pago</div>
                            <div className="p-5 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* {paymentMethods.map((pm) => (
                                        <label key={pm} className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50 cursor-pointer">
                                            <input type="radio" name="payment" checked={form.payment_method === pm} onChange={() => onChange("payment_method", pm)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
                                            <span className="text-sm sm:text-base capitalize">{pm === "culqi" ? "Culqi" : pm === "yape" ? "Yape" : pm.replace("_", " ")}</span>
                                        </label>
                                    ))} */}

                                    {Object.entries(paymentMethodsObject).map(([key, label]) => (
                                        <label
                                            key={key}
                                            className="flex items-center gap-3 p-3 border rounded-xl hover:bg-gray-50 cursor-pointer"
                                        >
                                            <input
                                                disabled={key === "yape" || key === "culqi" || key === "debit_card"}
                                                type="radio"
                                                name="payment"
                                                checked={form.payment_method === key}
                                                onChange={() => onChange("payment_method", key)}
                                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span className="text-sm sm:text-base">{label}</span>
                                        </label>
                                    ))}
                                </div>

                                {/* Campos condicionales según método */}
                                {(["credit_card", "debit_card", "culqi"]).includes(form.payment_method) && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="sm:col-span-2">
                                            <label htmlFor="card_number" className="block text-sm font-medium text-gray-700 mb-1">Número de tarjeta</label>
                                            <Input id="card_number" value={card.number} onChange={(e) => setCard((p) => ({ ...p, number: e.target.value }))} placeholder="0000 0000 0000 0000" />
                                        </div>
                                        <div>
                                            <label htmlFor="card_name" className="block text-sm font-medium text-gray-700 mb-1">Nombre en la tarjeta</label>
                                            <Input id="card_name" value={card.name} onChange={(e) => setCard((p) => ({ ...p, name: e.target.value }))} placeholder="Como figura en la tarjeta" />
                                        </div>
                                        <div>
                                            <label htmlFor="card_expiry" className="block text-sm font-medium text-gray-700 mb-1">Vencimiento (MM/AA)</label>
                                            <Input id="card_expiry" value={card.expiry} onChange={(e) => setCard((p) => ({ ...p, expiry: e.target.value }))} placeholder="MM/AA" />
                                        </div>
                                        <div>
                                            <label htmlFor="card_cvv" className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                                            <Input id="card_cvv" value={card.cvv} onChange={(e) => setCard((p) => ({ ...p, cvv: e.target.value }))} placeholder="***" />
                                        </div>
                                    </div>
                                )}

                                {form.payment_method === "yape" && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="sm:col-span-2">
                                            <label htmlFor="yape_phone" className="block text-sm font-medium text-gray-700 mb-1">Teléfono asociado a Yape</label>
                                            <Input id="yape_phone" value={yapePhone} onChange={(e) => setYapePhone(e.target.value)} placeholder="9xx xxx xxx" />
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notas del pedido (opcional)</label>
                                    <textarea id="notes" value={form.notes} onChange={(e) => onChange("notes", e.target.value)} placeholder="Instrucciones de entrega, horarios, etc." className="w-full rounded-xl border border-gray-300 px-3 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Columna derecha: resumen */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                            <div className="px-5 pt-5 text-base font-semibold">Resumen</div>
                            <div className="p-5 space-y-2 text-sm">
                                <div className="flex justify-between"><span>Subtotal</span><span>{new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(subtotal)}</span></div>
                                <div className="flex justify-between"><span>Envío</span><span>{new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(shipping)}</span></div>
                                {tax > 0 && (
                                    <div className="flex justify-between"><span>Impuestos</span><span>{new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(tax)}</span></div>
                                )}

                                {discount > 0 && (
                                    <div className="flex justify-between text-emerald-700"><span>Descuento</span><span>-{new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(discount)}</span></div>
                                )}
                                <hr className="my-3" />
                                <div className="flex justify-between text-base font-semibold">
                                    <span>Total</span>
                                    <span>{new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(total)}</span>
                                </div>
                            </div>
                            <div className="p-5 pt-0">
                                <Divider />
                                <Button type="submit" disabled={isProcessing} className="w-full py-3 text-base">
                                    {isProcessing ? "Procesando…" : "Confirmar pedido"}
                                </Button>
                                <p className="text-xs text-gray-500 mt-3">Al confirmar, aceptas nuestros términos y condiciones.</p>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CheckoutPage;
